import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem, CommandGroup } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Package, X, ChevronsUpDown } from "lucide-react";

/** Bloque F2/G1 — buscador de productos con autocompletado real contra GET /api/products?q=. */
export function ProductPicker({ value, onChange, placeholder = "Buscar producto…", testid }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value && (!selected || selected.id !== value)) {
      api.get(`/products/${value}`).then((r) => setSelected(r.data)).catch(() => {});
    }
    if (!value) setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.get("/products", { params: { q: term, limit: 8 } })
        .then((r) => setResults(r.data.items || r.data || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [term, open]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border px-2 py-1.5 bg-muted/50" data-testid={testid}>
        {selected.fotos?.[0] ? (
          <img src={selected.fotos[0].startsWith("http") ? selected.fotos[0] : fileUrl(selected.fotos[0])}
            alt="" className="h-7 w-7 rounded-lg object-cover" />
        ) : <Package className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm truncate flex-1">{selected.nombre}</span>
        <button type="button" onClick={() => { setSelected(null); onChange(null); }}
          data-testid={`${testid}-clear`} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" data-testid={testid}
          className="w-full justify-between rounded-xl font-normal text-muted-foreground">
          {placeholder}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Escribí para buscar…" value={term} onValueChange={setTerm} data-testid={`${testid}-input`} />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {results.map((p) => (
                <CommandItem key={p.id} value={p.id}
                  onSelect={() => { setSelected(p); onChange(p.id); setOpen(false); }}
                  data-testid={`${testid}-option-${p.id}`}>
                  {p.fotos?.[0] ? (
                    <img src={p.fotos[0].startsWith("http") ? p.fotos[0] : fileUrl(p.fotos[0])}
                      alt="" className="h-7 w-7 rounded-lg object-cover shrink-0" />
                  ) : <Package className="h-4 w-4 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm">{p.nombre}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.marca || "Sin marca"} · {p.stock_actual} u.</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
